import { CareerExtractionService } from '@/domains/students/careerExtraction.service';
import type {
  ProcessedStudentsResult,
  StudentResponseEntity,
} from '@/domains/students/student.entity';

/**
 * Use Case para procesar datos de estudiantes
 * Cumple SRP: Solo se encarga de transformar datos crudos en datos listos para UI
 *
 * Responsabilidades:
 * - Validar integridad de datos de entrada
 * - Coordinar servicios de dominio para preparar datos de estudiantes
 * - Extraer opciones de carrera desde los datos de estudiantes
 * - Proveer datos procesados en formato consistente para la UI
 */
export class ProcessStudentsUseCase {
  /**
   * Procesa la lista de estudiantes aplicando lógica de negocio
   * Extrae opciones de carrera y retorna datos listos para consumir en UI
   * @param students - Lista cruda de estudiantes desde API
   * @returns Datos procesados con estudiantes y opciones de carrera disponibles
   */
  execute(students: StudentResponseEntity[]): ProcessedStudentsResult {
    // Validación de entrada
    if (!this.isValidStudentData(students)) {
      return {
        students: [],
        careerOptions: [],
      };
    }

    // Aplicar lógica de dominio para extraer opciones de carrera
    const careerOptions =
      CareerExtractionService.extractCareerOptions(students);

    // Retornar datos procesados
    return {
      students,
      careerOptions,
    };
  }

  /**
   * Valida si los datos de estudiantes son válidos para procesar
   * @param students - Lista de estudiantes a validar
   * @returns true si los datos son un array no vacío, false en caso contrario
   */
  private isValidStudentData(students: StudentResponseEntity[]): boolean {
    return Array.isArray(students) && students.length > 0;
  }
}
